/**
 * ApiCustomTable Component
 * ---------------------------------------------------------
 * A universal, dynamic, multi-purpose table component used
 * across many modules (Users, Projects, Organization, API,
 * Replicate Contacts, Tasks, Call Logs, etc.)
 *
 * Features:
 * - Dynamic columns using react-table
 * - Infinite scrolling
 * - Row selection with multi-type logic
 * - Column visibility / ordering persistence
 * - Row-level actions (Edit, Toggle, Branch)
 * - Supports various entity types (User, Organization, API…)
 * - Light/Dark theme support
 * - Pagination, page size, footer information
 */
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import commonStyle from '../../Screens/common.module.css';
import { useTable, useColumnOrder } from 'react-table';
import { connect, useDispatch } from 'react-redux';
import TableHeader from '../TableHeader/TableHeader';
import CustomToggle from '../CustomToggle';
import { updateUserImport, updateUserStatus, EditUserTableHeader } from '../../Services/users';
import { updateAPIStatus, updateOrganizationStatus } from '../../Services/organizations';
import ColumnManagerModal from '../Modals/ColumnManagerModal/ColumnManager';
import { setClearFilter, setContactsSearchString, setTasksSearchString, setCallsSearchString, showSnackbarAction, updateFilterStatus } from '../../Redux/actions';
import { useHistory } from 'react-router';
import { FiEdit3 } from 'react-icons/fi';
import Loading from '../Loading/Loading';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useLocation } from 'react-router-dom';
import DistributionModel from '../Modals/DistributionModel/DistributionModel';
import { themeColors } from '../theme';
import { admin_mail } from '../../Values/constants';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

/**
 * Prop types for ApiCustomTable
 */
type props = {
  tableColumns: any[];
  tableRows: any[] | undefined;
  selectedRows: any[];
  setSelectedRows: (data: any[]) => void;
  tableType:
  | 'Organization'
  | 'Super admin'
  | 'User'
  | 'Task'
  | 'Project'
  | 'CallLogs'
  | 'API'
  | 'NEWS'
  | 'LeadDistributor'
  | 'DrillDown'
  | 'Replicate Contacts';
  showColumnModal: boolean;
  hideColumnModal: () => void;
  selectedRowsData?: any[];
  setSelectedRowsData?: (data: any[]) => void;

  organization: any;
  organizationUsers: any[];
  role: any;
  page: number;
  pageSize: number;
  setPage: (data: any) => void;
  setPageSize: (data: any) => void;
  isFinished: boolean;
  lastPage: number;
  totalCount: any;
  setBranch?: (text: boolean) => void;
  setUid?: (data: string) => void;
  user: any;
  setApiModal?: (data: boolean) => void;
  setApiEdit?: (data: boolean) => void;
  setApiData?: (data: any) => void;

  callApi?: (data: boolean, data2: Number | undefined) => Promise<void>;
  theme: any
  clearFilterTable?: boolean;
};

/**
 * Main component
 */
const ApiCustomTable: FunctionComponent<props> = ({
  tableColumns,
  tableRows,
  selectedRows,
  setSelectedRows,
  tableType,
  showColumnModal,
  hideColumnModal,
  selectedRowsData,
  setSelectedRowsData,
  organization,
  organizationUsers,
  role,
  setBranch,
  setUid,
  user,
  setApiModal,
  setApiEdit,
  setApiData,
  page,
  pageSize,
  setPage,
  setPageSize,
  callApi,
  isFinished,
  lastPage,
  totalCount,
  theme,
  clearFilterTable
}) => {
  /** Redux dispatcher */
  const dispatcher = useDispatch();

  /** Router navigation */
  const history = useHistory();

  /** Page size options dropdown */
  const pageSizeOptions = [10, 25, 50, 100, 200, 500];

  const [checked, setChecked] = useState(false);                // Select-all checkbox
  const [showDistributionModal, setShowDistributionModal] = useState(false);
  const [load, setLoad] = useState(false);                      // Loading overlay
  const [distributionData, setDistributionData] = useState();
  const [indexs, setIndex] = useState(0);
  const [pageData, setPageData] = useState<any[]>([]);          // Current paginated data

  const isMounted = React.useRef(false);

  /**
  * Reset filters when required (clearFilterTable changes)
  */
  useEffect(() => {
    if (isMounted.current) {
      clearFilter();
    } else {
      isMounted.current = true;
    }
  }, [clearFilterTable]);


  /**
   * Paginate data whenever page / pageSize / tableRows change
   */
  useEffect(() => {
    if (tableRows) {
      const data = tableRows.slice(pageSize * page, pageSize * page + pageSize);
      setPageData(data);
    }
  }, [page, pageSize, tableRows]);

  /**
 * "Select All" logic — supports multiple entity types.
 * Ensures unique rows are stored without duplicates.
 */
  const selectAll = (checked: boolean) => {

    const filterSelectedRows = (arr: any) => {
      let newArr = Array.from(new Set(arr.map((item: any) => item)));
      return newArr;
    }

    const uniqByProp_map = (prop: any) => (arr: any) =>
      Array.from(
        arr
          .reduce(
            (acc: any, item: any) => (
              item && item[prop] && acc.set(item[prop], item),
              acc
            ),
            new Map()
          )
          .values()
      );
    const uniqueById = uniqByProp_map("contactId");

    /** ENTITY TYPES WITH UNIQUE SELECTION LOGIC */
    if (
      tableType === 'User' ||
      tableType === 'Project' ||
      tableType === 'Organization' ||
      tableType === 'CallLogs' ||
      tableType === 'Task' ||
      tableType === "Replicate Contacts"
    ) {

      /** If checked → Add page rows to existing selected rows */
      if (checked === true) {
        if (tableRows) {

          // Selected row IDs
          let selectRowsIndex: any[] = [...selectedRows];
          let newRowIndex = pageData.map((lead) => lead.contactId || lead.project_id);
          let allSelectedRows = [...newRowIndex, ...selectRowsIndex];
          let filteredRows = filterSelectedRows(allSelectedRows);

          // Unique filtering for "User"
          if (tableType === "User") {
            setSelectedRows(filteredRows);
          } else {
            setSelectedRows(allSelectedRows);
          }
        }

        if (tableRows) {

          /** For selected row objects (detailed data) */
          if (selectedRowsData) {
            let selectRows: any[] = [...selectedRowsData];
            pageData.forEach((item: any) => {
              selectRows.push(item);
            });
            let filteredRowsData = uniqueById(selectRows);
            if (tableType === "User") {
              setSelectedRowsData && setSelectedRowsData(filteredRowsData);
            } else {
              setSelectedRowsData && setSelectedRowsData(selectRows);
            }
          } else {
            let selectRows: any[] = [];
            pageData.forEach((item: any) => {
              selectRows.push(item);
            });
            let filteredRowsData = uniqueById(selectRows);
            if (tableType === "User") {
              setSelectedRowsData && setSelectedRowsData(filteredRowsData);
            } else {
              setSelectedRowsData && setSelectedRowsData(selectRows);
            }
          }
        }
      } else {
        setSelectedRows([]);
        setSelectedRowsData && setSelectedRowsData([]);
      }
    } else {
      if (checked === true) {
        setSelectedRows(Array.from(Array(pageData.length).keys()));
      } else {
        setSelectedRows([]);
      }
    }
  };

  /**
 * Memoized columns + table data
 */
  const columns = useMemo(() => tableColumns, [tableColumns]);
  const data = useMemo(() => pageData, [pageData]);
  const [currentLimit, setCurrentLimit] = useState(20);
  const [active, setActiveUsers] = useState(0);

  /**
 * react-table initialization
 */
  const tableInstance: any = useTable(
    {
      columns,
      data,
    },
    useColumnOrder
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    allColumns,
    setColumnOrder,
    toggleHideAllColumns,
  } = tableInstance;

  /**
  * Load saved column order from localStorage
  */
  useEffect(() => {
    const savedColumns = localStorage.getItem('columns');
    if (savedColumns) {
      setColumnOrder(JSON.parse(savedColumns));
    }
    // eslint-disable-next-line
  }, []);


  /**
 * Load saved column order for specific tables
 * (User, Task, CallLogs, Project, Replicate Contacts)
 */
  useEffect(() => {
    let storageColumn;
    if (
      tableType === 'CallLogs' &&
      user.calllogsColumn &&
      user.calllogsColumn.length > 0
    ) {
      storageColumn = user.calllogsColumn;
      setColumnOrder(storageColumn);
    } else if (
      tableType === 'User' || tableType === 'Replicate Contacts' &&
      user.userColumn &&
      user.userColumn.length > 0
    ) {
      storageColumn = user.userColumn;
      setColumnOrder(storageColumn);
    } else if (
      tableType === 'Project' &&
      user.userColumn &&
      user.userColumn.length > 0
    ) {
      storageColumn = user.userColumn;
      setColumnOrder(storageColumn);
    } else if (
      tableType === 'Task' &&
      user.taskColumn &&
      user.taskColumn.length > 0
    ) {
      storageColumn = user.taskColumn;
      setColumnOrder(storageColumn);
    }
  }, [user, tableType]);

  /**
 * Count active organization users (for role-based checks)
 */
  useEffect(() => {
    if (organizationUsers) {
      let activeUsers = organizationUsers.filter(
        (item) => item.status === 'ACTIVE'
      );
      setActiveUsers(activeUsers.length);
    }
  }, [organizationUsers]);

  /**
  * Persist updated column ordering to backend/localStorage
  */
  const setColumns = (columnList: any) => {
    setColumnOrder(columnList);
    if (
      tableType === 'User' ||
      tableType === 'Project' ||
      tableType === 'CallLogs' ||
      tableType === 'Task' ||
      tableType === 'Replicate Contacts'
    ) {
      EditUserTableHeader(user.uid, tableType.toLowerCase(), columnList);
    }
    localStorage.setItem('columns', JSON.stringify(columnList));
  };

  /**
  * Check if user can be deactivated → ensures no dependent users
  */
  const checkStatus = (email: string) => {
    const l = data.filter((item) => item.reporting_to === email);
    if (l.length > 0) {
      return false;
    } else {
      return true;
    }
  };


  /**
   * Toggle active/inactive for organization/API/users
   */
  const onToggle = (event: any, row: any) => {

    // ORGANIZATION users toggle
    if (tableType === 'Organization') {
      setLoad(true);
      if (event.target.checked) {
        let check = checkStatus(row.original.user_email);
        if (check === true) {
          updateUserStatus(row.original.uid, 'INACTIVE', (data) =>
            setLoad(data)
          );
        } else {
          dispatcher(
            showSnackbarAction(
              'Some Users Are Reporting To This User!!',
              'warning'
            )
          );
          setLoad(false);
        }
      } else {
        let activeUsers = organizationUsers.filter(
          (item) => item.status === 'ACTIVE'
        );

        if (activeUsers.length === Number(organization.no_of_employees)) {
          dispatcher(
            showSnackbarAction(
              'Maximum active users limit reached!!',
              'warning'
            )
          );
          setLoad(false);
        } else {
          updateUserStatus(row.original.uid, 'ACTIVE', (data) => setLoad(data));
        }
      }
    }

    // SUPERADMIN organization toggle
    if (tableType === 'Super admin') {
      setLoad(true);
      if (event.target.checked) {
        updateOrganizationStatus(
          row.original.organization_id,
          'INACTIVE',
          (data) => setLoad(data)
        );
      } else {
        updateOrganizationStatus(
          row.original.organization_id,
          'ACTIVE',
          (data) => setLoad(data)
        );
      }
    }

    // API key toggle
    if (tableType === 'API') {
      setLoad(true);
      if (event.target.checked) {
        updateAPIStatus(row.original.api_key, 'INACTIVE', (data) =>
          setLoad(data)
        );
      } else {
        updateAPIStatus(row.original.api_key, 'ACTIVE', (data) =>
          setLoad(data)
        );
      }
    }
  };

  /**
 * Toggle “import” property for Lead Manager
 */
  const changeImport = (event: any, row: any) => {
    setLoad(true);
    if (event.target.checked) {
      updateUserImport(
        row.original.organization_id,
        row.original.user_email,
        true,
        dispatcher,
        (data) => setLoad(data)
      );
    } else {
      updateUserImport(
        row.original.organization_id,
        row.original.user_email,
        false,
        dispatcher,
        (data) => setLoad(data)
      );
    }
  };

  /**
 * Handle checkbox selection for each row
 */
  const onCheck = (event: any, row: any) => {

    /** ENTITY TYPES WITH CUSTOM SELECTION */
    if (
      tableType === 'User' ||
      tableType === 'Project' ||
      tableType === 'Organization' ||
      tableType === 'Task' ||
      tableType === "CallLogs" ||
      tableType === "Replicate Contacts"
    ) {
      if (event.target.checked) {
        let data = [...selectedRows];
        data.push(row.contactId || row.project_id);
        setSelectedRows(data);

        /** Also maintain selectedRowsData (full row data) */
        if (selectedRowsData) {
          let rowData = [...selectedRowsData];
          rowData.push(row);
          setSelectedRowsData && setSelectedRowsData(rowData);
        }
      } else if (!event.target.checked) {
        let data = [...selectedRows];
        let item = selectedRows.indexOf(row.contactId || row.project_id);
        if (item > -1) {
          data.splice(item, 1);
        }
        setSelectedRows(data);
        if (selectedRowsData) {
          let rowData = [...selectedRowsData];
          let val = selectedRowsData.indexOf(row);
          if (val > -1) {
            rowData.splice(val, 1);
          }
          setSelectedRowsData && setSelectedRowsData(rowData);
        }
      }
    }

    if (event.target.checked) {
      let data = [...selectedRows];
      data.push(row.contactId || row.project_id);
      setSelectedRows(data);
    } else if (!event.target.checked) {
      let data = [...selectedRows];
      let item = selectedRows.indexOf(row.contactId || row.project_id);
      if (item > -1) {
        data.splice(item, 1);
      }
      setSelectedRows(data);
    }
  };

  /**
 * Clears all filters and resets localStorage
 */
  const clearFilter = () => {
    /** Reset search strings */
    if (tableType === 'User') {
      dispatcher(setContactsSearchString(''));
      dispatcher(updateFilterStatus(true));
    }
    if (tableType === 'Replicate Contacts') {
      dispatcher(setContactsSearchString(''));
      dispatcher(updateFilterStatus(true));
    }
    if (tableType === 'Project') {
      dispatcher(setContactsSearchString(''));
    }
    if (tableType === 'Task') {
      dispatcher(setTasksSearchString(''));
    }
    if (tableType === 'CallLogs') {
      dispatcher(setCallsSearchString(''));
    }
    var key: any;
    for (var i = 0; i < localStorage.length; i++) {
      key = localStorage.key(i);

      if (key !== 'columns' && key !== 'isLightMode' && key !== 'totalMonths' && key !== 'lastAlertTime') {
        localStorage.removeItem(key);
      }
    }
    dispatcher(setClearFilter(true));
    if (tableType === 'User') {
    }
    if (tableType === 'Project') {
      history.push('/projects');
    }
  };

  /**
  * Theme-based dynamic styles
  */
  const tdStyles = {
    backgroundColor: theme ? themeColors.tdBackgroundColor : themeColors.tdBackgroundColorDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  }

  const footerStyles = {
    backgroundColor: theme ? themeColors.thBackgroundColor : themeColors.thBackgroundColorDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  }

  const buttonStyles = {
    backgroundColor: theme ? themeColors.buttonPrimary : themeColors.buttonPrimaryDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  }

  const trBorderStyles = {
    borderBottom: theme ? `1px solid ${themeColors.trBorderColor}` : `1px solid ${themeColors.trBorderColorDark}`,
  }

  /**
 * ----------------------------
 *        JSX START
 * ----------------------------
 */
  return (
    <>
      {/* Main table wrapper */}
      <div className={commonStyle.child} id="scrollable">

        {/* Loading Overlay */}
        {load === true && <Loading />}

        {/* Table container */}
        <div style={{ width: 'max-content' }} {...getTableProps()}>

          {/* HEADER SECTION */}
          <TableHeader
            headerGroups={headerGroups}
            selectAll={(data) => {
              selectAll(data);
              setChecked(data);
            }}
            panel={tableType}
            allCheck={checked}
          />

          {/* INFINITE SCROLL BODY */}
          <InfiniteScroll
            style={{ overflow: 'visible', minHeight: "100vh", width: "100%", marginBottom: "-10rem", paddingRight: '100px', ...tdStyles }}
            dataLength={currentLimit}
            next={() => setCurrentLimit(currentLimit + 20)}
            hasMore={rows.length > currentLimit}
            loader={''}
            scrollableTarget="scrollable"
            className={commonStyle.infiniteScroll}
          >

            {/* TABLE BODY */}
            <div {...getTableBodyProps()}>
              {rows.slice(0, currentLimit).map((row: any, index: number) => {
                prepareRow(row);
                return (
                  <div
                    {...row.getRowProps()}
                    style={
                      row.original.status === 'INACTIVE'
                        ? {
                          display: 'flex',
                          flexDirection: 'row',
                          ...trBorderStyles
                        }
                        : {
                          display: 'flex',
                          flexDirection: 'row',
                          ...trBorderStyles
                        }
                    }
                  >

                    {/* Sticky left column (checkbox + edit + index) */}
                    <div className={commonStyle.sticky} style={tdStyles} >

                      {/* Row select checkbox */}
                      <input
                        style={{ marginRight: "10px", accentColor: "#272944" }}
                        type="checkbox"
                        onChange={(e) => {
                          onCheck(e, row.original);
                        }}
                        checked={selectedRows.includes(row.original.contactId || row.original.project_id)}
                      />

                      {/* EDIT ICON (if allowed) */}
                      {tableType !== 'Task' && tableType !== 'Replicate Contacts' && user?.user_email !== admin_mail && (
                        <FiEdit3
                          size={15}
                          color="#272944"
                          style={{ marginRight: '7px' }}
                          onClick={() => {
                            // Handles navigation to Edit pages
                            if (tableType === 'Organization') {
                              history.push({
                                pathname: '/addUsers',
                                state: {
                                  detail: row.original,
                                  edit: true,
                                },
                              });
                            } else if (tableType === 'User') {
                              if (row.original.transfer_status === true) {
                                dispatcher(
                                  showSnackbarAction(
                                    'Lead With Transfer Status TRUE Cannot be Edited!!',
                                    'error'
                                  )
                                );
                              } else {
                                let data = { ...row.original };
                                history.push({
                                  pathname: '/editContacts',
                                  state: {
                                    detail: data,
                                  },
                                });
                              }
                            } else if (tableType === 'Project') {
                              if (row.original.transfer_status === true) {
                                dispatcher(
                                  showSnackbarAction(
                                    'Lead With Transfer Status TRUE Cannot be Edited!!',
                                    'error'
                                  )
                                );
                              } else {
                                let data = { ...row.original };
                                history.push({
                                  pathname: '/editProjects',
                                  state: {
                                    detail: data,
                                  },
                                });
                              }
                            } else if (tableType === 'Super admin') {
                              history.push({
                                pathname: '/editOrganizations',
                                state: { detail: row.original },
                              });
                            } else if (tableType === 'LeadDistributor') {
                              setShowDistributionModal(true);
                              setDistributionData(row.original);
                              setIndex(index);
                            } else if (tableType === 'API') {
                              setApiEdit && setApiEdit(true);
                              setApiModal && setApiModal(true);
                              setApiData && setApiData(row.original);
                            }
                          }}
                        />
                      )}

                      {/* Distribution Modal */}
                      {showDistributionModal && (
                        <DistributionModel
                          open={showDistributionModal}
                          organization_id={user.organization_id}
                          close={() => {
                            setShowDistributionModal(false);
                          }}
                          data={distributionData}
                          index={indexs}
                        />
                      )}

                      {/* Row Number */}
                      {pageSize * page + index + 1}
                    </div>

                    {/* TABLE CELLS */}
                    {row.cells.map((cell: any) => {
                      return (
                        <div
                          className={commonStyle.td}

                          {...cell.getCellProps()}
                          style={
                            cell.column.Header === 'News Link'
                              ? { minWidth: '490px', ...tdStyles }
                              : tdStyles
                          }

                          onClick={() => {
                            /**
                        * Handle clicking on Customer/Project Name → navigate to detail pages
                        */
                            if (
                              (cell.column.Header === 'Customer Name' &&
                                tableType === 'User') ||
                              (cell.column.Header === 'Customer Name' &&
                                tableType === 'Replicate Contacts') ||
                              (cell.column.Header === 'Customer Name' &&
                                tableType === 'Task') ||
                              (cell.column.Header === 'Customer Name' &&
                                tableType === 'CallLogs')
                            ) {
                              if (cell.row.original.transfer_status === true) {
                                dispatcher(
                                  showSnackbarAction(
                                    'This is a Transfered Lead!!',
                                    'error'
                                  )
                                );
                              } else {
                                if (user.user_first_name !== "SuperAdmin" && user.user_first_name !== "Team") {
                                  history.push({
                                    pathname: '/contactDetails',
                                    state: {
                                      detail: {
                                        ...cell.row.original,
                                        created_at:
                                          cell.row.original[
                                            'created_at'
                                          ].toDate(),
                                        lead_assign_time: cell.row.original[
                                          'lead_assign_time'
                                        ]
                                          ? cell.row.original[
                                            'lead_assign_time'
                                          ].toDate()
                                          : '',
                                        next_follow_up_date_time: cell.row
                                          .original['next_follow_up_date_time']
                                          ? cell.row.original[
                                            'next_follow_up_date_time'
                                          ].toDate()
                                          : '',
                                        modified_at: cell.row.original[
                                          'modified_at'
                                        ]
                                          ? cell.row.original[
                                            'modified_at'
                                          ].toDate()
                                          : '',
                                        stage_change_at: cell.row.original[
                                          'stage_change_at'
                                        ]
                                          ? cell.row.original[
                                            'stage_change_at'
                                          ].toDate()
                                          : '',
                                        feedback_time: cell.row.original[
                                          'feedback_time'
                                        ]
                                          ? cell.row.original[
                                            'feedback_time'
                                          ].toDate()
                                          : '',
                                        due_date: cell.row.original['due_date']
                                          ? cell.row.original['due_date'].toDate
                                            ? cell.row.original[
                                              'due_date'
                                            ].toDate()
                                            : ''
                                          : '',
                                      },
                                    },
                                  });
                                }

                              }
                            } else if (
                              (cell.column.Header === 'Project Name' &&
                                tableType === 'Project')
                            ) {
                              if (cell.row.original.transfer_status === true) {
                                dispatcher(
                                  showSnackbarAction(
                                    'This is a Transfered Lead!!',
                                    'error'
                                  )
                                );
                              } else {
                                history.push({
                                  pathname: "/projectDetails",
                                  state: {
                                    "AllProjectdata": tableRows,
                                    detail: {
                                      ...cell.row.original,
                                      created_at: cell.row.original[
                                        "created_at"
                                      ]
                                        ? cell.row.original[
                                          "created_at"
                                        ].toDate()
                                          ? cell.row.original[
                                            "created_at"
                                          ].toDate()
                                          : ""
                                        : "",
                                    },
                                  },
                                })
                              }
                            }
                          }}
                        >
                          {cell.render('Cell')}
                        </div>
                      );
                    })}

                    {/* Column: Toggle Active/Inactive */}
                    {tableType === 'Organization' && (
                      <div
                        className={commonStyle.td}
                        style={tdStyles}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            paddingRight: '10px',
                            justifyContent: 'space-between',
                          }}
                        >
                          {(tableType === 'Organization' ||
                            tableType === 'Super admin' ||
                            tableType === 'API') && (
                              <CustomToggle
                                size="small"
                                onChange={(e) => onToggle(e, row)}
                                checked={
                                  row.original.status === 'INACTIVE'
                                    ? true
                                    : false
                                }
                              />
                            )}
                        </div>
                      </div>
                    )}

                    {/* Column: Lead Manager Import Checkbox */}
                    {tableType === 'Organization' ? (
                      <div className={commonStyle.tdSmall} style={tdStyles}>
                        {row.original.profile === 'Lead Manager' ? (
                          <input
                            style={{ marginRight: "10px", accentColor: "#272944" }}
                            type="checkbox"
                            onChange={(e) => changeImport(e, row)}
                            checked={
                              row.original.import === true ? true : false
                            }
                          />
                        ) : null}
                      </div>
                    ) : null}

                    {/* Column: Branch Button */}
                    {tableType === 'Organization' ? (
                      <div
                        className={commonStyle.tdSmall}
                        style={{ paddingLeft: '30px', ...tdStyles }}
                      >
                        {row.original.profile === 'Lead Manager' ? (
                          <button
                            className={commonStyle.branchButton}
                            onClick={() => {
                              setBranch && setBranch(true);
                              setUid && setUid(row.original.uid);
                            }}
                          >
                            Branch
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </InfiniteScroll>
        </div>
      </div>

      {/* FOOTER SECTION */}
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: "center", borderRadius: "0.5rem", padding: "0.5rem", marginTop: "1rem", marginBottom: "-1rem", ...footerStyles }}>
        <div className={commonStyle.footer_table} style={{ height: "35px" }}>

          {/* Selected count */}
          <p className={commonStyle.countHead} style={{ fontSize: "12px", paddingTop: "7px" }} >
            Selected Records: {selectedRows.length}
          </p>
          <div style={{
            display: "flex",
            alignItems: "center",
            margin: "0 0 0 0"
          }}>
            <span>
              Page <strong>{page + 1 + '  '}</strong>
            </span>

            {/* Page size dropdown */}
            <select
              value={pageSize}
              className={commonStyle.dropDownSelecct}
              style={{
                fontSize: "12px",
                fontWeight: 400,
                height: "24px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                background: "#fff",
                color: "#222",
                margin: "0 4px"
              }}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
            >
              {pageSizeOptions.map((pagesize) => (
                <option key={pagesize} value={pagesize}>
                  Show {pagesize}
                </option>
              ))}
            </select>

            {/* Prev Page */}
            <button
              className={commonStyle.addBox}
              onClick={() => {
                setChecked(false);
                if (page !== 0) {
                  setPage(page - 1);
                }
              }}
              disabled={!page}
              style={{
                width: "32px",
                height: "24px",
                minWidth: "32px",
                padding: 0,
                backgroundColor: !page ? "#e5e7eb" : buttonStyles.backgroundColor,
                color: buttonStyles.color,
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                cursor: page ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 2px"
              }}
            >
              <ChevronLeftIcon style={{ fontSize: "16px" }} />
            </button>

            {/* Next Page */}
            <button
              className={commonStyle.addBox}
              onClick={async () => {
                setChecked(false);
                setPage(page + 1);
              }}
              disabled={page === lastPage}
              style={{
                width: "32px",
                height: "24px",
                minWidth: "32px",
                padding: 0,
                backgroundColor: page === lastPage ? "#e5e7eb" : buttonStyles.backgroundColor,
                color: buttonStyles.color,
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                cursor: page === lastPage ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 2px"
              }}
            >
              <ChevronRightIcon style={{ fontSize: "16px" }} />
            </button>
          </div>

          {/* Total count */}
          <p style={{ fontSize: "12px", paddingTop: "7px" }} className={commonStyle.countHead}>
            Total Records: {totalCount ? totalCount : 0}
          </p>
        </div>
      </div>

      {/* Column Manager Modal */}
      {showColumnModal === true && (
        <ColumnManagerModal
          columnsList={allColumns}
          open={showColumnModal}
          close={hideColumnModal}
          onChangeColumns={(data: any[]) => setColumns(data)}
          toggleAllColumns={toggleHideAllColumns}
        />
      )}
    </>
  );
};


/**
 * Map Redux state to props
 */
const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    organizationUsers: state.organizationUsers.data,
    role: state.user.role,
    organization: state.organization,
    theme: state.theme.isLightMode
  };
};

export default connect(mapStateToProps)(ApiCustomTable);
